import { useStoreActiveCameras } from "@/components/common/view-runtime-context";
import { useCameraParametersIfAvailable } from "@/components/r3f/utils/camera-parameters";
import type { MiniMapActions } from "@/components/r3f/utils/minimap/minimap";
import { setFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import {
  setCompareElementId,
  setCompareSceneFilter,
} from "@/store/modes/walk-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import { useAppDispatch } from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import { View } from "@faro-lotv/app-component-toolbox";
import { useThree } from "@react-three/fiber";
import type { JSX } from "react";
import { useCallback, useMemo, useRef } from "react";
import { useOverlayElements } from "../overlay-elements-context";
import { SplitMinMap } from "../split-mode/split-minimap";
import { useReproportionCameraOnUnmount } from "../split-mode/split-scene";
import type { SplitInitialState, SplitState } from "../split-mode/split-state";
import { FlySplitView, type FlySplitViewActions } from "./fly-split-view";
import type { FlyWalkWaypoint } from "./flywalk-types";
import { ToolsAcquaintanceContextProvider } from "./tools/tools-acquaintance-context";
import { useFlySplitSceneCameras } from "./use-fly-split-scene-cameras";

/** @returns A component that renders two distinct views on elements of the same project. */
export function FlySplitScene({
  mainScene,
  mainWalkElement,
  compareScene,
  compareWalkElement,
  initialState,
  mainSceneFilter,
  compareSceneFilter,
}: SplitState & { initialState: SplitInitialState | undefined }): JSX.Element {
  const dispatch = useAppDispatch();

  const onMainWaypointClicked = useCallback(
    (waypoint: FlyWalkWaypoint) => {
      dispatch(setActiveElement(waypoint.id));
      dispatch(setFlyWalkSceneFilter(SceneFilter.Pano));
    },
    [dispatch],
  );

  const onCompareWaypointClicked = useCallback(
    (waypoint: FlyWalkWaypoint) => {
      dispatch(setCompareElementId(waypoint.id));
      dispatch(setCompareSceneFilter(SceneFilter.Pano));
    },
    [dispatch],
  );

  return (
    <ToolsAcquaintanceContextProvider>
      <FlySplitSceneImpl
        mainScene={mainScene}
        mainWalkElement={mainWalkElement}
        compareScene={compareScene}
        compareWalkElement={compareWalkElement}
        initialState={initialState}
        mainSceneFilter={mainSceneFilter}
        compareSceneFilter={compareSceneFilter}
        onMainWaypointClicked={onMainWaypointClicked}
        onCompareWaypointClicked={onCompareWaypointClicked}
      />
    </ToolsAcquaintanceContextProvider>
  );
}

type FlySplitSceneImplProps = SplitState & {
  initialState: SplitInitialState | undefined;
  /** Callback issued when a waypoint is clicked in the left view */
  onMainWaypointClicked(waypoint: FlyWalkWaypoint): void;
  /** Callback issued when a waypoint is clicked in the right view*/
  onCompareWaypointClicked(waypoint: FlyWalkWaypoint): void;
};

function FlySplitSceneImpl({
  mainScene,
  mainWalkElement,
  compareScene,
  compareWalkElement,
  initialState,
  mainSceneFilter,
  compareSceneFilter,
  onMainWaypointClicked,
  onCompareWaypointClicked,
}: FlySplitSceneImplProps): JSX.Element {
  const background = useThree((s) => s.scene.background);

  // Computing all cameras needed in splitscreen mode
  const { leftViewCameras, rightViewCameras } = useFlySplitSceneCameras();

  // Updating both left and right cameras according to a possible given deep link
  useCameraParametersIfAvailable(leftViewCameras.camera, initialState?.camera);
  useCameraParametersIfAvailable(
    rightViewCameras.camera,
    initialState?.rightCam,
  );

  const cameras = useMemo(
    () => [leftViewCameras.camera, rightViewCameras.camera],
    [leftViewCameras.camera, rightViewCameras.camera],
  );

  // Storing the active cameras in a context used to compute deep links on demand
  useStoreActiveCameras(cameras);

  const { firstScreen, secondScreen, miniMap, minimapCanvas } =
    useOverlayElements();
  const miniMapActions = useRef<MiniMapActions>(null);

  // The left camera is the default r3f camera used also for other modes.
  // On unmount, the 'View' component writes an invalid aspect ratio prop
  // to the camera. Therefore, on unmount we need to reproportion the left
  // camera according to the r3f canvas' size.
  useReproportionCameraOnUnmount(leftViewCameras.camera);

  const leftViewActions = useRef<FlySplitViewActions>(null);
  const rightViewActions = useRef<FlySplitViewActions>(null);

  const placeholderClickedInMinimap = useCallback(
    (waypoint: FlyWalkWaypoint) => {
      leftViewActions.current?.onPanoClickedInMinimap(waypoint);
      rightViewActions.current?.onPanoClickedInMinimap(waypoint);
    },
    [],
  );

  return (
    <>
      <View
        camera={leftViewCameras.camera}
        trackingElement={firstScreen}
        background={background}
        hasSeparateScene
      >
        <FlySplitView
          currentScene={mainScene}
          sceneFilter={mainSceneFilter}
          isSecondaryView={false}
          onWaypointClicked={onMainWaypointClicked}
          actions={leftViewActions}
          cameras={leftViewCameras}
        />
      </View>
      <View
        camera={rightViewCameras.camera}
        trackingElement={secondScreen}
        background={background}
        hasSeparateScene
      >
        <FlySplitView
          currentScene={compareScene}
          sceneFilter={compareSceneFilter}
          isSecondaryView
          onWaypointClicked={onCompareWaypointClicked}
          actions={rightViewActions}
          cameras={rightViewCameras}
        />
      </View>
      {miniMap && secondScreen && (
        <SplitMinMap
          trackingElement={miniMap}
          canvasElement={minimapCanvas}
          actions={miniMapActions}
          mainWalkElement={mainWalkElement}
          compareWalkElement={compareWalkElement}
          mainSceneFilter={mainSceneFilter}
          compareSceneFilter={compareSceneFilter}
          leftCamera={leftViewCameras.camera}
          rightCamera={rightViewCameras.camera}
          miniMapCameraPosition={undefined}
          onPlaceholderClicked={placeholderClickedInMinimap}
        />
      )}
    </>
  );
}
